import { startCase } from "lodash";

import type { Account } from "~/v2/api/rest/account";

export interface AccountOption {
  id: string;
  label: string;
  accountNumber?: string;
  balance?: string;
  product: "live" | "paper";
}

const accountBalanceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatAccountBalance = (
  value: string | number | null | undefined,
): string | undefined => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return undefined;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? accountBalanceFormatter.format(numericValue)
    : undefined;
};

const getLiveAccountName = (account: Account): string => {
  const explicitName = account.account_name?.trim();
  if (explicitName) {
    return explicitName;
  }
  if (account.account_type === "ira") {
    const subType = startCase(account.account_sub_type ?? "").trim();
    return subType ? `${subType} IRA` : "IRA";
  }
  if (account.account_type === "legal_entity") {
    return "Business";
  }
  return "Individual";
};

const getPaperAccountName = (account: Account): string => {
  const name = account.account_name?.trim();
  return name ? `Paper · ${name}` : "Paper";
};

export const buildAccountHeaderLabel = (
  account: Account,
  product: AccountOption["product"],
): string =>
  product === "paper"
    ? getPaperAccountName(account)
    : getLiveAccountName(account);

export const buildAccountOptions = ({
  liveAccounts,
  paperAccounts,
}: {
  liveAccounts: Account[];
  paperAccounts: Account[];
}): AccountOption[] => {
  const live = liveAccounts.map((account) => ({
    id: account.id,
    label: getLiveAccountName(account),
    accountNumber: account.account_number ?? undefined,
    balance: formatAccountBalance(account.equity),
    product: "live" as const,
  }));

  const paper = paperAccounts.map((account) => ({
    id: account.id,
    label: getPaperAccountName(account),
    accountNumber: account.account_number ?? undefined,
    balance: formatAccountBalance(account.equity),
    product: "paper" as const,
  }));

  return [...live, ...paper];
};
